import { Button, ButtonSpinner, ButtonText } from "@/components/ui/button";
import { Fab, FabIcon, FabLabel } from "@/components/ui/fab";
import { Heading } from "@/components/ui/heading";
import { HStack } from "@/components/ui/hstack";
import { CloseIcon, Icon } from "@/components/ui/icon";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import {
  getOrgPaymentRuleControllerFindOneQueryKey,
  useOrgPaymentRuleControllerFindOne,
  useOrgPaymentRuleControllerUpdate,
  useOrgQRControllerList,
  useOrgWalletControllerList,
  useOrganizationControllerFindOne,
  useOrganizationParticipantsControllerFindAll,
  useOrganizationTagsControllerGetTags,
} from "@/generated/api/api";
import { GripVertical, HelpCircle, PenBox, Plus } from "lucide-react-native";
import {
  ComponentPropsWithoutRef,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DragList from "react-native-draglist";
import { RestaurantTipsSharingItem } from "./restaurant.tipssharing.item";
import { PaymentRuletDTODataDistribution } from "@/generated/api/models";
import { Pressable } from "@/components/ui/pressable";
import { EmptyState } from "@/screens/common/empty.state";
import { Platform, ScrollView } from "react-native";
import { Alert, AlertIcon, AlertText } from "@/components/ui/alert";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/utils/useToast";
import { RestaurantTipsSharingCreateEditModal } from "./restaurant.tipssharing.modal";
import {
  AnyPaymentVersionedDistribution,
  PaymentVersionedDistribution,
  PaymentVersionedRule,
  RuleFromToDataProps,
} from "./restaurant.tipssharing.types";
import { useMobileBreakpoint } from "@/utils/useMobileBreakpoint";
import { Divider } from "@/components/ui/divider";

export type RestaurantTipsSharingListFullProps = {
  organizationId: string;
};

function HeaderSeparator({
  title,
  subtitle,
  modal,
}: {
  title: string;
  subtitle: string;
  modal: { title: string; description: ReactNode };
}) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <VStack className="gap-1">
        <Pressable onPress={() => setShowModal(true)}>
          <HStack className="items-center gap-2">
            <Heading>{title}</Heading>
            <Icon as={HelpCircle} />
          </HStack>
        </Pressable>
        <Text size="sm">{subtitle}</Text>
      </VStack>
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
        }}
        size="md"
      >
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader>
            <Heading size="md" className="text-typography-950">
              {modal.title}
            </Heading>
            <ModalCloseButton>
              <Icon
                as={CloseIcon}
                size="md"
                className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
              />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            {typeof modal.description === "string" ? (
              <Text size="sm" className="text-typography-500">
                {modal.description}
              </Text>
            ) : (
              modal.description
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              onPress={() => {
                setShowModal(false);
              }}
            >
              <ButtonText>Understood</ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export function RestaurantTipsSharingListFull(
  props: RestaurantTipsSharingListFullProps
) {
  const {
    data: ruleListData,
    isRefetching,
    refetch,
  } = useOrgPaymentRuleControllerFindOne(props.organizationId);

  const { data: organization } = useOrganizationControllerFindOne(
    props.organizationId
  );

  const { data: tagsResponse } = useOrganizationTagsControllerGetTags(
    props.organizationId
  );

  const { data: walletListData } = useOrgWalletControllerList(
    props.organizationId
  );

  const { data: participantsData } =
    useOrganizationParticipantsControllerFindAll(props.organizationId);

  const { data: qrsData } = useOrgQRControllerList(props.organizationId);

  const { mutate, isPending } = useOrgPaymentRuleControllerUpdate();

  const [createEditIsOpen, setCreateEditIsOpen] = useState(false);
  const [createEditRule, setCreateEditRule] = useState<PaymentVersionedRule>();

  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const defaultRuleList = useMemo<PaymentVersionedDistribution>(() => {
    const { distribution } = ruleListData?.data ?? {
      version: 1,
      distribution: {
        byOrganization: [],
        byParticipant: [],
        byTag: [],
      },
    };

    return {
      byOrganization: {
        data: distribution.byOrganization.map((r) => ({
          id: getPaymentRuleReactId(r),
          ...r,
        })),
        version: 0,
      },
      byParticipant: {
        data: distribution.byParticipant.map((r) => ({
          id: getPaymentRuleReactId(r),
          ...r,
        })),
        version: 0,
      },
      byTag: {
        data: distribution.byTag.map((r) => ({
          id: getPaymentRuleReactId(r),
          ...r,
        })),
        version: 0,
      },
    };
  }, [ruleListData?.data]);

  const [distribution, setDistribution] = useState(defaultRuleList);

  useEffect(() => {
    setDistribution(defaultRuleList);
  }, [defaultRuleList]);

  const distributionCumulativeVersion =
    distribution.byOrganization.version +
    distribution.byParticipant.version +
    distribution.byTag.version;

  const hasChanges = distributionCumulativeVersion > 0;

  const onSave = useCallback(() => {
    mutate(
      {
        organizationId: props.organizationId,
        data: {
          version: 1,
          distribution: {
            byOrganization: distribution.byOrganization.data,
            byParticipant: distribution.byParticipant.data,
            byTag: distribution.byTag.data,
          },
        },
      },
      {
        onSuccess: (data) => {
          showToast(
            "success",
            "Tips sharing rules has been successfully updated"
          );
          queryClient.setQueryData(
            getOrgPaymentRuleControllerFindOneQueryKey(props.organizationId),
            () => data
          );
        },
      }
    );
  }, [
    distributionCumulativeVersion,
    props.organizationId,
    queryClient,
    showToast,
  ]);

  const dataProps: RuleFromToDataProps | undefined = organization
    ? {
        organization,
        participants: participantsData ?? [],
        wallets: walletListData ?? [],
        qrs: qrsData ?? [],
        tags: tagsResponse ?? { public: [], private: [] },
      }
    : undefined;

  const [dragActive, setDragActive] = useState(false);
  const [activePage, setActivePage] = useState(0);
  const scrollViewRef = useRef<ScrollView>(null);
  const [scrollViewSize, setScrollViewSize] = useState<{
    width: number;
    height: number;
  }>();

  const onDistributionAction = useCallback(
    (
      action:
        | { createUpdate: Omit<PaymentVersionedRule, "id"> & { id?: string } }
        | { delete: string }
    ) => {
      setDistribution((distribution) => {
        const filter = <T extends keyof PaymentRuletDTODataDistribution>(
          k: T,
          id: string
        ) => {
          const index = distribution[k].data.findIndex((r) => r.id === id);

          if (index !== -1) {
            distribution[k].data.splice(index, 1);
            return {
              ...distribution[k],
              version: distribution[k].version + 1,
            };
          }

          return distribution[k];
        };

        if ("createUpdate" in action) {
          let key: keyof PaymentVersionedDistribution;
          let shouldScrollTo: number | undefined;

          switch (action.createUpdate.from.type) {
            case "organization":
              key = "byOrganization";
              shouldScrollTo = activePage !== 1 ? 1 : undefined;
              break;
            case "participant":
              key = "byParticipant";
              shouldScrollTo = activePage !== 0 ? 0 : undefined;
              break;
            case "tag":
              key = "byTag";
              shouldScrollTo = activePage !== 2 ? 2 : undefined;
              break;
          }

          if (shouldScrollTo !== undefined && scrollViewSize?.width) {
            scrollViewRef.current?.scrollTo({
              animated: true,
              x: shouldScrollTo * scrollViewSize.width,
            });
          }

          if (action.createUpdate.id) {
            const index = distribution[key].data.findIndex(
              (r) => r.id === action.createUpdate.id
            );
            if (index !== -1) {
              const newData = [...distribution[key].data];

              newData[index] = {
                ...action.createUpdate,
                // Update id
                id: getPaymentRuleReactId(action.createUpdate as any),
              } as any;

              return {
                ...distribution,
                [key]: {
                  data: newData,
                  version: distribution[key].version + 1,
                },
              };
            } else {
              // Probably from value was changed, so we couldn't find in the same key. we have to
              // delete from other sections this rule and create new one

              const newDistribution = {
                byOrganization:
                  key === "byOrganization"
                    ? distribution[key]
                    : filter("byOrganization", action.createUpdate.id),
                byParticipant:
                  key === "byParticipant"
                    ? distribution[key]
                    : filter("byParticipant", action.createUpdate.id),
                byTag:
                  key === "byTag"
                    ? distribution[key]
                    : filter("byTag", action.createUpdate.id),
              };

              const newData = [...newDistribution[key].data];

              newData.push({
                ...action.createUpdate,
                id: getPaymentRuleReactId(action.createUpdate as any),
              } as any);

              return {
                ...newDistribution,
                [key]: {
                  data: newData,
                  version: newDistribution[key].version + 1,
                },
              };
            }
          } else {
            return {
              ...distribution,
              [key]: {
                data: [
                  ...distribution[key].data,
                  {
                    ...action.createUpdate,
                    id: getPaymentRuleReactId(action.createUpdate as any),
                  } as any,
                ],
                version: distribution[key].version + 1,
              },
            };
          }
        }

        return {
          byOrganization: filter("byOrganization", action.delete),
          byParticipant: filter("byParticipant", action.delete),
          byTag: filter("byTag", action.delete),
        };
      });
    },
    [setDistribution, activePage]
  );

  const bp = useMobileBreakpoint();

  const getRuleList = ({
    type,
    header,
  }: {
    type: PaymentVersionedRule["from"]["type"];
    header: ReactElement;
  }) => {
    return (
      dataProps && (
        <RuleList
          {...dataProps}
          from={type}
          distribution={distribution}
          onChange={setDistribution}
          onPressRule={(r) => {
            setCreateEditRule(r);
            setCreateEditIsOpen(true);
          }}
          onDragBegin={() => setDragActive(true)}
          onDragEnd={() => setDragActive(false)}
          scrollViewSize={scrollViewSize}
          ListHeaderComponent={() => header}
          {...(!hasChanges &&
            Platform.OS !== "web" && {
              refreshing: isRefetching,
              onRefresh: refetch,
            })}
        />
      )
    );
  };

  const tabs = useMemo(() => ["Personal QR", "Business QR", "Tag"], []);

  return (
    <>
      <VStack className="gap-2 w-full h-full">
        {hasChanges && (
          <Alert
            action="info"
            className="gap-4 flex-row flex py-2 items-center self-stretch mt-4 mx-4"
          >
            <AlertIcon as={PenBox} className="mt-1" />
            <HStack className="justify-between flex-1 items-center gap-1 sm:gap-8">
              <VStack className="flex-1">
                <AlertText
                  className="font-semibold text-typography-900"
                  size="md"
                >
                  Apply changes?
                </AlertText>
              </VStack>
              <Button size="sm" onPress={onSave} isDisabled={isPending}>
                <ButtonText>Save</ButtonText>
                {isPending && <ButtonSpinner />}
              </Button>
            </HStack>
          </Alert>
        )}

        <VStack className="w-full px-4">
          <HStack space="lg" className="w-full gap-4 items-stretch min-h-fit">
            {tabs.map((tab, index) => {
              return (
                <Pressable
                  key={tab}
                  className={`flex-1 py-2 ${
                    activePage === index ? "border-b-[3px]" : "border-b-0"
                  } border-outline-900 hover:border-b-[3px] ${
                    activePage === index
                      ? "hover:border-outline-900"
                      : "hover:border-outline-200"
                  } `}
                  onPress={() => {
                    if (scrollViewSize) {
                      scrollViewRef.current?.scrollTo({
                        animated: true,
                        x: index * scrollViewSize.width,
                      });
                    }
                  }}
                >
                  <Text
                    size="md"
                    className={`${
                      activePage === index
                        ? "text-typography-900"
                        : "text-typography-600"
                    } font-medium text-center`}
                  >
                    {tab}
                  </Text>
                </Pressable>
              );
            })}
          </HStack>
          <Divider />
        </VStack>

        <ScrollView
          ref={scrollViewRef}
          className="w-full h-full"
          contentContainerClassName="grow"
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
          scrollEnabled={!dragActive && bp === "mobile"}
          scrollEventThrottle={16}
          onScroll={(e) => {
            if (scrollViewSize?.width) {
              const scrollOffsetX = e.nativeEvent.contentOffset.x;
              const currentPage = Math.round(
                scrollOffsetX / scrollViewSize.width
              );
              if (activePage != currentPage) {
                setActivePage(currentPage);
              }
            }
          }}
          onLayout={(e) => {
            setScrollViewSize({
              width: e.nativeEvent.layout.width,
              height: e.nativeEvent.layout.height,
            });
          }}
        >
          {getRuleList({
            type: "participant",
            header: (
              <HeaderSeparator
                title="Personal QR code"
                subtitle="Any tips, received from personal page of waiter/worker/employee will be distributed, according to these rules"
                modal={{
                  title: "Tips sharing",
                  description: (
                    <VStack className="gap-2">
                      <Text size="sm">
                        Every worker has their own personal QR code, assigned
                        specifically for this team. You may create rules
                        of tips sharing, according to which tips, received by
                        specific worker might be splitted and shared to across
                        team.
                      </Text>
                      <VStack className="">
                        <Text size="sm">
                          For example: Whenever{" "}
                          <Text size="sm" bold>
                            Anne Sampson
                          </Text>{" "}
                          receive tips:
                        </Text>
                        <Text size="sm">
                          1. 25% of them goes to our hostess -{" "}
                          <Text size="sm" bold>
                            Lisa Brandon
                          </Text>
                        </Text>
                        <Text size="sm">
                          2. 15% of them goes to tag -{" "}
                          <Text size="sm" bold>
                            Bar
                          </Text>
                        </Text>
                      </VStack>

                      <Text size="sm" italic>
                        Please note, that tips routed to tag{" "}
                        <Text size="sm" bold>
                          Bar
                        </Text>{" "}
                        will be distributed equally on each worker, assigned
                        with this tag, however this could be also adjusted.
                        Please refer to the section "Tags".
                      </Text>
                    </VStack>
                  ),
                }}
              />
            ),
          })}
          {getRuleList({
            type: "organization",
            header: (
              <HeaderSeparator
                title="Business page or custom QR code"
                subtitle="Any tips, received from business page or custom QR code will be distributed, according to these rules"
                modal={{
                  title: "Tips sharing",
                  description: (
                    <VStack className="gap-2">
                      <Text size="sm">
                        Business has main QR code and may have unlimited
                        number of custom QR codes. You may create rules of tips
                        sharing, according to which tips, received from
                        business main QR code or any other custom QR-code
                        might be splitted and shared across team.
                      </Text>
                      <VStack className="">
                        <Text size="sm">
                          For example: Whenever{" "}
                          <Text size="sm" bold>
                            {organization?.name ?? "Business"}
                          </Text>{" "}
                          receive tips:
                        </Text>
                        <Text size="sm">
                          1. 25% of them goes to our hostess -{" "}
                          <Text size="sm" bold>
                            Lisa Brandon
                          </Text>
                        </Text>
                        <Text size="sm">
                          2. 15% of them goes to tag -{" "}
                          <Text size="sm" bold>
                            Bar
                          </Text>
                        </Text>
                        <Text size="sm">
                          3. 60% (rest) of them goes to wallet -{" "}
                          <Text size="sm" bold>
                            Tip Jar
                          </Text>
                        </Text>
                      </VStack>

                      <Text size="sm" italic>
                        Please note, that tips routed to wallet{" "}
                        <Text size="sm" bold>
                          Tip Jar
                        </Text>{" "}
                        shall be distributed later manually across team. This
                        might be helpful if you would like to distribute tips,
                        based on certain data, such as shifts, efforts and
                        efficiency.
                      </Text>
                    </VStack>
                  ),
                }}
              />
            ),
          })}
          {getRuleList({
            type: "tag",
            header: (
              <HeaderSeparator
                title="Team tags"
                subtitle="Any tips routed to tags by other rules, will be distributed, according to these rules"
                modal={{
                  title: "Tips sharing",
                  description: (
                    <VStack className="gap-2">
                      <Text size="sm">
                        Tips, received from any page or QR-code, might be routed
                        to tag, which will equally distribute amount to all
                        workers, assigned with this tag. You may create rules of
                        tips sharing, according to which tips, routed to tag,
                        will be distributed with your preferences.
                      </Text>
                      <VStack>
                        <Text size="sm">
                          For example: Whenever{" "}
                          <Text size="sm" bold>
                            {organization?.name ?? "Business"}
                          </Text>{" "}
                          receive tips:
                        </Text>
                        <Text size="sm">
                          1. 25% of them goes to our hostess -{" "}
                          <Text size="sm" bold>
                            Lisa Brandon
                          </Text>
                        </Text>
                        <Text size="sm">
                          2. 75% (rest) of them goes to tag -{" "}
                          <Text size="sm" bold>
                            Bar
                          </Text>
                          . This tag assigned to two bartenders -{" "}
                          <Text size="sm" bold>
                            Michael
                          </Text>{" "}
                          and{" "}
                          <Text size="sm" bold>
                            Alex
                          </Text>
                          .
                        </Text>
                      </VStack>

                      <Text size="sm">
                        By default, 75% of original tips will be equally
                        distributed between two bartenders (50%, 50%), however
                        you may want to adjust that, with the following rules:
                      </Text>
                      <VStack>
                        <Text size="sm">
                          Whenever tag{" "}
                          <Text size="sm" bold>
                            Bar
                          </Text>{" "}
                          receive tips:
                        </Text>
                        <Text size="sm">
                          1. 80% goes to first bartender -{" "}
                          <Text size="sm" bold>
                            Michael
                          </Text>
                        </Text>
                        <Text size="sm">
                          2. 20% (rest) goes to second bartender -{" "}
                          <Text size="sm" bold>
                            Alex
                          </Text>
                        </Text>
                      </VStack>
                    </VStack>
                  ),
                }}
              />
            ),
          })}
        </ScrollView>
        <Fab
          onPress={() => {
            setCreateEditRule(undefined);
            setCreateEditIsOpen(true);
          }}
        >
          <FabIcon as={Plus} />
          <FabLabel>Add rule</FabLabel>
        </Fab>
      </VStack>
      {dataProps && (
        <RestaurantTipsSharingCreateEditModal
          {...dataProps}
          rule={createEditRule}
          onClose={() => setCreateEditIsOpen(false)}
          preferredFromType={
            activePage === 0
              ? "participant"
              : activePage === 1
              ? "organization"
              : "tag"
          }
          isOpen={createEditIsOpen}
          onChange={(rule) => {
            onDistributionAction({ createUpdate: rule });
          }}
          onDelete={(id) => {
            onDistributionAction({ delete: id });
          }}
        />
      )}
    </>
  );
}

export const getPaymentRuleValue = (
  input?: PaymentVersionedRule["from"] | PaymentVersionedRule["to"]
) => {
  switch (input?.type) {
    case "organization":
      return input.qrId ?? "";
    case "participant":
      return input.id;
    case "tag":
      return input.tag;
    case "wallet":
      return input.id;
  }
};

export const getPaymentRuleReactId = (
  r: NonNullable<
    PaymentRuletDTODataDistribution[keyof PaymentRuletDTODataDistribution]
  >[number],
  timestamp = new Date().getTime()
) =>
  `${getPaymentRuleValue(r.from)}-${r.percent}-${getPaymentRuleValue(
    r.to
  )}-${timestamp}`;

function RuleList({
  from,
  distribution,
  onChange,
  onPressRule,
  onDragBegin: externalOnDragBegin,
  onDragEnd: externalOnDragEnd,
  scrollViewSize,
  onRefresh,
  refreshing,
  ListHeaderComponent,
  ...dataProps
}: RuleFromToDataProps & {
  from: PaymentVersionedRule["from"]["type"];
  distribution: PaymentVersionedDistribution;
  onPressRule: (rule: PaymentVersionedRule) => void;
  onChange: (arg: SetStateAction<PaymentVersionedDistribution>) => void;
  onDragBegin?: () => void;
  onDragEnd?: () => void;
  scrollViewSize?: { width: number; height: number };
} & Pick<
    ComponentPropsWithoutRef<typeof DragList>,
    "ListHeaderComponent" | "refreshing" | "onRefresh"
  >) {
  const key: keyof PaymentVersionedDistribution = useMemo(() => {
    switch (from) {
      case "organization":
        return "byOrganization";
      case "participant":
        return "byParticipant";
      case "tag":
        return "byTag";
    }
  }, [from]);

  const data: AnyPaymentVersionedDistribution = distribution[key];

  const styleProps: Pick<
    ComponentPropsWithoutRef<typeof DragList>,
    "className" | "containerStyle" | "contentContainerClassName" | "style"
  > = Platform.OS === "web"
    ? {
        className: "h-full",
        containerStyle: { flex: 1, height: "100%", width: "100%" },
        contentContainerClassName: "grow gap-2 py-4 px-4",
        style: {
          maxWidth: scrollViewSize?.width,
          width: scrollViewSize?.width,
          height: "100%",
        },
      }
    : {
        className: "h-full px-4",
        containerStyle: { flex: 1, height: "100%", width: "100%" },
        contentContainerClassName: "grow gap-2 py-4",
        style: {
          maxWidth: scrollViewSize?.width,
          width: scrollViewSize?.width,
          height: "100%",
        },
      };

  return (
    <DragList<PaymentVersionedRule>
      {...styleProps}
      data={data.data}
      onRefresh={onRefresh}
      refreshing={refreshing}
      renderItem={({ item, isActive, onDragStart, onDragEnd }) => {
        const dragBegin = () => {
          onDragStart();
          externalOnDragBegin?.();
        };

        const dragEnd = () => {
          onDragEnd();
          externalOnDragEnd?.();
        };

        return (
          <Pressable
            {...(Platform.OS !== "web"
              ? {
                  onLongPress: dragBegin,
                  onPressOut: dragEnd,
                }
              : undefined)}
            disabled={isActive}
            onPress={() => onPressRule(item)}
          >
            <RestaurantTipsSharingItem
              data={item}
              {...dataProps}
              draggableNode={
                <Pressable
                  onPressIn={dragBegin}
                  {...(Platform.OS === "web"
                    ? { onTouchEnd: dragEnd }
                    : { onPressOut: dragEnd })}
                >
                  <Icon as={GripVertical} />
                </Pressable>
              }
            />
          </Pressable>
        );
      }}
      keyExtractor={(info) => info.id}
      ListHeaderComponent={ListHeaderComponent}
      ListEmptyComponent={() => {
        return <EmptyState title="No rules yet" />;
      }}
      onReordered={(from, to) => {
        onChange((distribution) => {
          const { version, data } = distribution[key];

          const copy = [...data]; // Don't modify react data in-place
          const removed = copy.splice(from, 1);
          copy.splice(to, 0, removed[0]); // Now insert at the new pos

          return {
            ...distribution,
            [key]: {
              version: version + 1,
              data: copy,
            },
          };
        });
      }}
      // onDragEnd={({ data }) =>
      //   onChange((distribution) => {
      //     const { version } = distribution[key];

      //     return {
      //       ...distribution,
      //       [key]: {
      //         version: version + 1,
      //         data,
      //       },
      //     };
      //   })
      // }
    />
  );
}
